import type {ReactElement, ReactNode} from 'react';
import type {AiChatSidebarProps} from './ai-chat-sidebar.types';
import styles from './ai-chat-sidebar.module.scss';
import {FlatIconColor, FlatIconColorType, IconType} from '@Components/icon-v2/icon.types';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {ClickableDiv} from '@Components/clickable-div';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {SkeletonBrickTheme} from '@Components/skeleton-brick';
import {Thumbnail} from '@Components/thumbnail';
import {getAssetUrl} from '@Utils/s3.util';
import {onLogoClick} from '@Libraries/ai-chat.library';
import {getAiChatBasePath} from '@/ai-chat/lib/chat-history-library';
import {SidebarTrigger} from './components/sidebar-trigger/sidebar-trigger';

// Whether the collapsed rail renders at all is owned by the parent (ai-chat.tsx renderSideBar);
// this component always renders its rail when mounted.
export function AiChatSidebar({...props}: AiChatSidebarProps): ReactElement {
  const {state, actions} = useAiChat();

  const getLogo = (): ReactNode => {
    return (
      <ClickableDiv
        onClick={(e): void => {
          e.stopPropagation();
        }}
      >
        <Thumbnail
          data={{
            type: ThumbnailType.IMAGE,
            imageSrc: getAssetUrl('pmw-logo-small-dark.png'),
            showSkeletonLoading: true,
            skeletonLoadingTheme: SkeletonBrickTheme.LIGHT,
          }}
          className={`spacing-p-2 _cursor-pointer radius-8 ${styles.sidebarLogo}`}
          onClick={onLogoClick}
        />
      </ClickableDiv>
    );
  };

  const getTopOptions = (): ReactNode => {
    return (
      <div className={'flex-v-row gap-2 '}>
        {getLogo()}
        <SidebarTrigger
          tooltip={window.i18next.t('pmwjs_open_sidebar')}
          icon="icon-sidebar"
          onClick={(e): void => {
            e.stopPropagation();
            trackGA4CustomEvent(GA4EventName.AICHAT_ALL_CHATS);
            actions.setIsSidebarExpanded(true);
          }}
        />
        <SidebarTrigger
          tooltip={window.i18next.t('pmwjs_new_chat')}
          icon="icon-plus"
          href={getAiChatBasePath()}
          onClick={(e): void => {
            e.stopPropagation();
            actions.activateForNewChat();
            actions.setCurrentChatId(undefined);
          }}
        />
        <SidebarTrigger
          tooltip={window.i18next.t('pmwjs_search')}
          icon="icon-search"
          onClick={(e): void => {
            e.stopPropagation();
            actions.setIsSidebarExpanded(true);
            actions.setShouldFocusSearch(true);
          }}
        />
      </div>
    );
  };

  const getQuickTipsTrigger = (): ReactNode => {
    return (
      <SidebarTrigger
        tooltip={window.i18next.t('pmwjs_quick_tips')}
        icon="icon-bulb"
        iconClassName={state.isQuickTipsView ? 'radius-4' : 'radius-8'}
        type={state.isQuickTipsView ? IconType.FLAT : IconType.SECONDARY}
        flatIconType={state.isQuickTipsView ? {color: FlatIconColor.PRIMARY, type: FlatIconColorType.DEFAULT} : undefined}
        onClick={(e): void => {
          e.stopPropagation();
          actions.navigateToQuickTips();
        }}
      />
    );
  };

  return (
    <ClickableDiv
      className={`_full-height spacing-p-1 spacing-p-b-2 flex-v-row flex-justify-between _cursor-pointer ${styles.sidebarContainer} ${props.className ?? ''}`}
      onClick={() => {
        trackGA4CustomEvent(GA4EventName.AICHAT_ALL_CHATS);
        actions.setIsSidebarExpanded(true);
      }}
    >
      {getTopOptions()}
      {getQuickTipsTrigger()}
    </ClickableDiv>
  );
}
